import { Box, Typography } from "@mui/material";
import React, { useRef } from "react";
import { useSnackbar } from "notistack";
import { useCallback } from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import CustomizedTab from "../../../components/Custom/CustomizedTab";
import PurchaseOrderTable from "../../../components/Table/DocumentTable/PurchaseOrderTable";
import { getAllPurchaseOrder } from "../../../features/Purchase/PurchaseOrder/purchase-order-actions";
import { purchaseOrderActions } from "../../../features/Purchase/PurchaseOrder/purchase-order-slice";
import { filterDateWithUnix } from "../../../utils/filterparams";
import { usePermission } from "../../../hooks/use-auth";

const PurchaseOrder = ({ report }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const gridRef = useRef();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isFilter = searchParams.get("filter");
  const navigate = useNavigate();
  const { createPermission } = usePermission();

  const breadcrumbs = [
    {
      name: t("purchase.index"),
      to: "/purchase",
    },
    {
      name: t("purchase.order.index"),
    },
  ];

  report &&
    breadcrumbs.splice(1, 0, {
      name: t("purchase.report.index"),
      to: "/purchase/report",
    });

  const currentTab = pathname + (isFilter ? `?filter=${isFilter}` : "");

  const tabs = [
    {
      label: t("inventory.all"),
      path: `${pathname}`,
    },
    {
      label: t("status.draft"),
      path: `${pathname}?filter=draft`,
    },
    {
      label: t("status.waitApprove"),
      path: `${pathname}?filter=waitApprove`,
    },
    {
      label: t("status.notApproved"),
      path: `${pathname}?filter=notApproved`,
    },
    {
      label: t("status.approved"),
      path: `${pathname}?filter=approved`,
    },
    {
      label: t("status.partiallyImported"),
      path: `${pathname}?filter=partiallyImported`,
    },
    {
      label: t("status.fullyImported"),
      path: `${pathname}?filter=fullyImported`,
    },
    {
      label: t("status.cancelled"),
      path: `${pathname}?filter=cancelled`,
    },
  ];

  const datasource = {
    getRows(params) {
      const request = params.request;
      const { filterModel } = request;
      const {
        document_id,
        contact_document_id,
        contact_name,
        issue_date,
        due_date,
        expect_date,
        created_by,
        employee_list,
        ...otherFilterModel
      } = filterModel;

      const formatFilterModel = {
        ...otherFilterModel,
        document_id: document_id && {
          ...document_id,
          mode: "insensitive",
        },
        contact_document_id: contact_document_id && {
          ...contact_document_id,
          mode: "insensitive",
        },
        // contact: contact_name && {
        //   operator: "OR",
        //   condition1: {
        //     filterType: "object",
        //     filter: {
        //       contact_name_1: contact_name,
        //     },
        //   },
        //   condition2: {
        //     filterType: "object",
        //     filter: {
        //       contact_name_2: contact_name,
        //     },
        //   },
        // },
        contact: contact_name && {
          filterType: "object",
          filter: {
            contact_name_1: {
              ...contact_name,
              mode: "insensitive",
            },
          },
        },
        issue_date: issue_date && filterDateWithUnix(issue_date),
        due_date: due_date && filterDateWithUnix(due_date),
        expect_date: expect_date && filterDateWithUnix(expect_date),
        employee_list: employee_list && {
          filterType: "objectArray",
          type: "some",
          filter: {
            first_name: { ...employee_list, mode: "insensitive" },
          },
        },
        created_by: created_by && {
          filterType: "object",
          filter: {
            first_name: {
              ...created_by,
              mode: "insensitive",
            },
          },
        },
      };

      dispatch(
        getAllPurchaseOrder(
          {
            startRow: request.startRow,
            endRow: request.endRow,
            filterModel: formatFilterModel,
            sortModel: request.sortModel,
          },
          params,
          enqueueSnackbar
        )
      );
    },
  };

  const onFilterChanged = useCallback(
    (params) => {
      const instance = params.api.getFilterInstance("render_status");
      switch (isFilter) {
        case "draft":
          instance?.setModel({ values: ["draft"] });
          break;
        case "waitApprove":
          instance?.setModel({ values: ["PENDING"] });
          break;
        case "notApproved":
          instance?.setModel({ values: ["DECLINED"] });
          break;
        case "approved":
          instance?.setModel({ values: ["approved"] });
          break;
        case "partiallyImported":
          instance?.setModel({ values: ["partiallyImported"] });
          break;
        case "fullyImported":
          instance?.setModel({ values: ["fullyImported"] });
          break;
        case "cancelled":
          instance?.setModel({ values: ["cancelled"] });
          break;
        default:
          instance?.setModel({
            values: [
              "draft",
              "PENDING",
              "DECLINED",
              "approved",
              "partiallyImported",
              "fullyImported",
            ],
          });
          break;
      }
      params.api.onFilterChanged();
    },
    [isFilter]
  );

  const onGridReady = (params) => {
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
    return () => {
      dispatch(purchaseOrderActions.loadedAllPurchaseOrder());
    };
  }, [dispatch, onFilterChanged]);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ my: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">{t("purchase.order.index")}</Typography>
        {!report && createPermission && (
          <CustomizedButton
            title={t("purchase.order.add")}
            variant="contained"
            onClick={() => navigate(`${pathname}/add`)}
          />
        )}
      </Box>
      {!report && (
        <CustomizedTab table tabs={tabs} currentTab={currentTab} divider />
      )}
      <PurchaseOrderTable gridRef={gridRef} onGridReady={onGridReady} />
    </>
  );
};

export default PurchaseOrder;
